import type {
    FilterStates,
    FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import { SEARCH_CATEGORIES } from '@modules-rocco/search/config/search-service.config';
import { URL_PARAM_MAP } from '@modules-rocco/filters/config/filter-url.config';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSearchQueryFilterState,
    isSingleValueFilterState,
} from '@modules-rocco/filters/filter-state/filter-state';
import type { SingleValueFilterState } from '@modules-rocco/filters/filter-state/single-value-filter-state';
import type { MultipleValuesFilterState } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import type { SearchableListFilterState } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import type { RangeFilterState } from '@modules-rocco/filters/filter-state/range-filter-state';
import type { FilterState } from '@modules-rocco/filters/filter-state/filter-state';
import type { SearchQueryFilterState } from '@modules-rocco/filters/filter-state/search-query-filter-state';
import type { LocationFilterState } from '@modules-rocco/filters/filter-state/location-filter-state';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import type { URLParamValues } from '@modules-rocco/url/types';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import type { FeatureFlags } from '@modules/root';

/**
 * Create a new URL based on the filters' state and original URL
 * @param filterStates the filter states
 * @param originalURL
 * @param ignoreFilterKeys we will ignore these keys when parsing the filter states
 * @param defaultSort
 * @param featureSet
 * @returns
 */
export const mapFilterStatesToURL = (
    filterStates: FilterStates,
    originalURL: string,
    ignoreFilterKeys: FilterStatesKey[] = [],
    defaultSort?: SearchSortOptions,
    featureSet: FeatureFlags = []
): string => {
    const isCategoryPage = !!SEARCH_CATEGORIES.find(
        (cfg) => cfg.categoryLink && originalURL.startsWith(cfg.categoryLink)
    );

    // we will not add the category param if it's category page
    // instead, we will create new path based on the category state
    const params = convertFilterStateToURLParams(
        filterStates,
        isCategoryPage ? [...ignoreFilterKeys, 'category'] : ignoreFilterKeys,
        defaultSort
    );

    if (filterStates.shouldUpdateUserPreferences) {
        params.updateUserPrefs = true;
    }

    const { basePath, params: originalParams } = parseURL(originalURL);

    // keep original info for the non category page
    if (!isCategoryPage) {
        if (originalParams?.category) {
            params.category = originalParams.category.value;
        } else {
            if (params.forSale || params.downloads) {
                params.category = 'products';
            }
        }

        if (originalParams?.search) {
            params.search = String(originalParams.search.value);
        }
    }

    const categoryState = filterStates.states.category;

    // create new path based on the category state for the category page
    const newPath = isCategoryPage
        ? categoryState?.selectedCategory?.url ?? basePath
        : basePath;

    return generateURL(newPath, params, true, featureSet);
};

const convertFilterStateToURLParams = (
    filterStates: FilterStates,
    ignoreFilterKeys: FilterStatesKey[] = [],
    defaultSort?: SearchSortOptions
): URLParamValues => {
    const ignoreKeys = [...ignoreFilterKeys, 'search', 'category'];

    // general states (not facet filters)
    const stateParams = Object.keys(filterStates.states).reduce(
        (acc, filterKey) => {
            if (ignoreKeys.includes(filterKey)) {
                return acc;
            }

            const paramKey = filterKey as Exclude<FilterStatesKey, 'search'>;
            const urlParamKey = getMappingKeyName(filterKey);
            const state = filterStates.states?.[paramKey];

            if (!state) {
                return acc;
            }

            if (Array.isArray(state)) {
                return { ...acc, [urlParamKey]: state } as URLParamValues;
            }

            return {
                ...acc,
                ...convertFilterByState(urlParamKey, state),
            };
        },
        {} as URLParamValues
    );

    // facet filters
    const facetParams = filterStates.facet?.facetOptions
        ? filterStates.facet.facetOptions.reduce(
              (facetAcc, singleState) => ({
                  ...facetAcc,
                  ...convertFilterByState(
                      'facetOptions',
                      singleState.state,
                      singleState.id
                  ),
              }),
              {} as URLParamValues
          )
        : [];

    // sort
    const sortParamName = getMappingKeyName('sortBy');
    const sortParams: URLParamValues =
        filterStates.sortBy && filterStates.sortBy !== defaultSort
            ? {
                  [sortParamName]: filterStates.sortBy,
              }
            : {};

    return { ...stateParams, ...facetParams, ...sortParams } as URLParamValues;
};

const convertFilterByState = (
    filterKey: string,
    state: FilterState,
    subFilterKey?: string
): URLParamValues => {
    if (isSingleValueFilterState(state)) {
        return convertSingleValueFilterState(filterKey, state);
    }

    if (isSearchableListFilterState(state)) {
        return convertSearchableListFilterState(filterKey, state, subFilterKey);
    }

    if (isMultipleValuesFilterState(state)) {
        return convertMultipleValuesFilterState(filterKey, state);
    }

    if (isRangeFilterState(state)) {
        return convertRangeFilterState(filterKey, state);
    }

    if (isSearchQueryFilterState(state)) {
        return convertSearchQueryFilterState(filterKey, state);
    }

    if (isLocationFilterState(state)) {
        return convertLocationFilterState(state);
    }

    throw new Error(
        '[filter-url-helpers] convertFilterByState: unsupported filter state:' +
            JSON.stringify(state)
    );
};

const convertSingleValueFilterState = (
    filterKey: string,
    state: SingleValueFilterState
): URLParamValues => {
    return state.checked ? { [filterKey]: 1 } : {};
};

const convertMultipleValuesFilterState = (
    filterKey: string,
    state: MultipleValuesFilterState
): URLParamValues => {
    if (!state.selectedItems?.length) {
        return {};
    }

    const values = state.selectedItems
        .filter((item) => !!item.value)
        .map((item) => item.value!);

    return {
        [filterKey]: state.allowMultipleSelectedItems
            ? values
            : values[0] ?? '',
    };
};

const convertSearchableListFilterState = (
    filterKey: string,
    state: SearchableListFilterState,
    subFilterKey?: string
): URLParamValues => {
    const params: URLParamValues = {};
    const subFilterPrefix = subFilterKey ? `${subFilterKey}-` : '';

    // temp disable
    // it never worked, and it doesn't have any impact on filtered products.
    // It just for saving state of filter search.
    // For example you have a suppliers list and you are filtering it using a query: "Architecture".
    // You are getting a list of suppliers which contains all suppliers matching the query + added URL query param `suppliers_search=Architecture`.
    // But prod doesn't use / restore this parameter into filter.

    // if (state.search.length > 0) {
    //     params[`${filterKey}_search`] = `${subFilterPrefix}${state.search}`;
    // }
    //
    // if (state?.items?.length > DEFAULT_SEARCH_LIST_SIZE) {
    //     params[`${filterKey}_size`] = state.items.length;
    // }

    const selectedItems = state?.selectedItems ?? [];

    if (selectedItems.length > 0) {
        const items = selectedItems.map(
            (item) => `${subFilterPrefix}${item.value}`
        );

        params[filterKey] = state.allowMultipleSelectedItems
            ? items
            : items[0] ?? '';
    }

    return params;
};

const convertRangeFilterState = (
    filterKey: string,
    state: RangeFilterState
): URLParamValues => {
    if (!state.value) {
        return {};
    }

    const { min, max } = state.value;
    const { min: availableMin, max: availableMax } = state.range;

    if (min === availableMin && max === availableMax) {
        return {};
    }

    return { [filterKey]: `${min},${max}` };
};

const convertSearchQueryFilterState = (
    filterKey: string,
    state: SearchQueryFilterState
): URLParamValues => {
    if (!state.query) {
        return {};
    }

    return { [filterKey]: state.query };
};

const convertLocationFilterState = (
    state: LocationFilterState
): URLParamValues => {
    if (!state.selectedLocation) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(state.selectedLocation)
            .map(([paramKey, value]) => {
                return [getMappingKeyName(paramKey), value];
            })
            .filter((entry) => entry[1])
    );
};

const getMappingKeyName = (key: string): string => {
    return URL_PARAM_MAP[key] ?? key;
};
